<script setup lang="ts">
import type { EventVault } from "@/models"
import { computed } from "vue"
import { useGetInvitationWelcome, useGetRsvpResponse, useGetWishlist } from "@/api"
import { InvitationWelcomeContent } from "@/components"
import { BaseModal, Button, ButtonLink, Card, Icon } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { GuestInvitationModalKey } from "@/symbols"

const props = defineProps<{
  eventVault: EventVault
}>()

const eventVaultId = computed(() => props.eventVault.id)

const { toggle } = useModal(GuestInvitationModalKey)

const { t } = useTranslation()

const { data: invitationWelcome, isLoading: isInvitationLoading } = useGetInvitationWelcome(undefined, undefined, eventVaultId)
const { data: wishlist } = useGetWishlist(eventVaultId)
const { data: rsvpResponse, isLoading: isRsvpLoading } = useGetRsvpResponse(eventVaultId)

const isInitialLoading = computed(() => isInvitationLoading.value || isRsvpLoading.value || !props.eventVault)

const isExistingRsvp = computed(() => Boolean(rsvpResponse.value?.rsvpDetails))

const coverImageStyle = computed(() => ({
  backgroundImage:
    props.eventVault?.coverImageUrl && !isInitialLoading.value
      ? `url(${props.eventVault.coverImageUrl})`
      : "var(--gpx-default-cover-image)",
}))
</script>

<template>
  <BaseModal
    :name="GuestInvitationModalKey"
    transition="flip-side"
    size="xl"
    :close-on-backdrop-click="true"
  >
    <div class="relative guest-layout">
      <Button
        shape="circle"
        variant="secondary"
        size="sm"
        class="absolute right-2 top-2 z-10"
        @click="toggle"
      >
        <Icon icon="Close" />
      </Button>

      <Card variant="secondary" :with-padding="false" class="min-w-full overflow-hidden rounded-xl shadow-xl">
        <div class="grid md:grid-cols-[1fr_1fr] md:min-h-140">
          <div v-if="isInitialLoading" class="flex flex-col gap-6 p-4 font-body md:p-6">
            <div class="gpx-skeleton h-8 w-3/4 md:h-10" />
            <div class="flex flex-col gap-2">
              <div class="gpx-skeleton h-4 w-full" />
              <div class="gpx-skeleton h-4 w-full" />
              <div class="gpx-skeleton h-4 w-5/6" />
              <div class="gpx-skeleton h-4 w-4/5" />
              <div class="gpx-skeleton h-4 w-3/4" />
            </div>
            <div class="flex shrink-0 flex-col gap-4 rounded-lg border border-primary-card-text p-4 font-medium">
              <div>
                <div class="flex items-end gap-4">
                  <div class="gpx-skeleton h-5 w-5 shrink-0" />
                  <div class="gpx-skeleton h-4 w-16" />
                </div>
                <div class="ml-10 mt-2">
                  <div class="gpx-skeleton mb-2 h-5 w-full" />
                  <div class="gpx-skeleton h-4 w-32" />
                </div>
              </div>
              <div>
                <div class="flex items-end gap-4">
                  <div class="gpx-skeleton h-5 w-5 shrink-0" />
                  <div class="gpx-skeleton h-4 w-24" />
                </div>
                <div class="ml-10 mt-2">
                  <div class="gpx-skeleton h-5 w-48" />
                </div>
              </div>
            </div>
          </div>

          <div v-else class="flex min-w-0 flex-col gap-4 p-4 font-body md:gap-5 md:p-6">
            <div class="flex min-w-0 flex-col gap-4 md:gap-5 md:pe-0 h-full">
              <InvitationWelcomeContent
                v-if="invitationWelcome"
                variant="compact"
                :invitation="invitationWelcome"
                :wishlist="wishlist"
              />

              <div v-if="invitationWelcome?.rsvpEnabled" class="mt-auto">
                <ButtonLink
                  :to="`/guest/${eventVaultId}/invitation?show_rsvp=true`"
                  prefix-icon="Envelope"
                  :disabled="isRsvpLoading"
                >
                  {{ isExistingRsvp ? t("invitation_page.update_rsvp_btn") : t("invitation_page.rsvp_btn") }}
                </ButtonLink>
              </div>
            </div>
          </div>

          <div
            class="hidden h-full min-h-48 w-auto bg-cover bg-center object-cover md:block md:min-h-0"
            :style="coverImageStyle"
          />
        </div>
      </Card>
    </div>
  </BaseModal>
</template>
